import type { ComponentChildren } from 'preact';
import { cn } from '@/lib/cn';

export interface SegmentedTab {
  id: string;
  label: ComponentChildren;
  href: string;
  disabled?: boolean;
}

export interface SegmentedTabsProps {
  tabs: ReadonlyArray<SegmentedTab>;
  activeId: string;
  onChange: (tab: SegmentedTab) => void;
  'aria-label'?: string;
  className?: string;
}

const BASE = 'inline-flex items-center gap-0.5 rounded-lg bg-surface-2 p-0.5';

const ITEM_BASE =
  'relative px-3 py-1.5 text-xs font-medium outline-none rounded-md transition-all duration-med';

const ITEM_STATES = {
  active: 'bg-surface text-fg-strong shadow-sm',
  inactive: 'text-fg-muted hover:text-fg-strong',
  focus: 'focus-visible:ring-2 focus-visible:ring-brand/50',
  disabled: 'opacity-50 cursor-not-allowed',
};

/**
 * SegmentedTabs — botones de navegación tipo "tab" con estado activo
 * y soporte para teclado (Arrow keys).
 *
 * Diferencia con SegmentedControl:
 * - SegmentedControl: alterna entre opciones discretas sin navegar (filtros, vistas).
 * - SegmentedTabs: navegación entre secciones/tabs con soporte de href.
 *
 * Ejemplo:
 * ```tsx
 * const [activeTab, setActiveTab] = useState('overview');
 * <SegmentedTabs
 *   tabs={[
 *     { id: 'overview', label: 'Overview' },
 *     { id: 'details', label: 'Details' },
 *   ]}
 *   activeId={activeTab}
 *   onChange={(tab) => setActiveTab(tab.id)}
 * />
 * ```
 */
export function SegmentedTabs({
  tabs,
  activeId,
  onChange,
  'aria-label': ariaLabel,
  className,
}: SegmentedTabsProps) {
  const handleKeyDown = (e: KeyboardEvent) => {
    const enabledTabs = tabs.filter((t) => !t.disabled);
    if (enabledTabs.length === 0) return;

    const currentIdx = enabledTabs.findIndex((t) => t.id === activeId);

    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIdx = (currentIdx + 1) % enabledTabs.length;
      onChange(enabledTabs[nextIdx]!);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIdx = (currentIdx - 1 + enabledTabs.length) % enabledTabs.length;
      onChange(enabledTabs[prevIdx]!);
    }
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(BASE, className)}
      onKeyDown={handleKeyDown}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeId;
        const isDisabled = tab.disabled;

        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-disabled={isDisabled}
            disabled={isDisabled}
            tabIndex={isActive ? 0 : -1}
            onClick={() => !isDisabled && onChange(tab)}
            className={cn(
              ITEM_BASE,
              ITEM_STATES.focus,
              isDisabled && ITEM_STATES.disabled,
              isActive ? ITEM_STATES.active : ITEM_STATES.inactive,
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
